import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Seo from '@/components/Seo/Seo';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import termsBg from '@/assets/terms.svg';
import { Section1 } from './sections/Section1';
import { Section2 } from './sections/Section2';
import { Section3 } from './sections/Section3';
import { Section4 } from './sections/Section4';
import { Section5 } from './sections/Section5';
import { Section6 } from './sections/Section6';
import { Section7 } from './sections/Section7';
import { Section8 } from './sections/Section8';
import { Section9 } from './sections/Section9';
import { Section10 } from './sections/Section10';
import { Section11 } from './sections/Section11';

const TERMS_URL = 'https://ukvineyardguide.co.uk/contractterms';

const SECTIONS = [
  { number: 1, title: 'Definitions' },
  { number: 2, title: 'Data Protection and Confidentiality' },
  { number: 3, title: 'Intellectual Property' },
  { number: 4, title: 'Cancellation' },
  { number: 5, title: 'Limitation of Liability' },
  { number: 6, title: 'Warranty Disclaimer' },
  { number: 7, title: 'Service Availability and Platform Changes' },
  { number: 8, title: 'Dispute Resolution' },
  { number: 9, title: 'Governing Law' },
  { number: 10, title: 'Entire Agreement' },
  { number: 11, title: 'Contact Information' },
];

export default function TermsAndConditionsPage(): JSX.Element {
  const [activeSection, setActiveSection] = useState<number>(1);
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    const handleScroll = () => {
      const threshold = 80;
      let nextActiveSection = 1;

      for (let i = 0; i < SECTIONS.length; i++) {
        const section = SECTIONS[i];
        if (!section) continue;

        const el = sectionRefs.current[section.number];
        if (!el) continue;

        const rect = el.getBoundingClientRect();

        if (rect.top <= threshold) {
          nextActiveSection = section.number;
        }
      }

      // Check if we're at the bottom of the page
      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 100;
      if (isAtBottom && SECTIONS.length > 0) {
        nextActiveSection =
          SECTIONS[SECTIONS.length - 1]?.number ?? nextActiveSection;
      }

      setActiveSection(nextActiveSection);
    };

    const timeoutId = setTimeout(() => {
      handleScroll();
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (number: number) => {
    sectionRefs.current[number]?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Seo
        title="Terms and Conditions – UK Vineyard Guide"
        description="General Terms and Conditions applicable to all Vineyard Listing and Commercial Partnership Agreements with UK Vineyard Guide Ltd."
        canonicalUrl={TERMS_URL}
      />

      <Box
        component="main"
        sx={{
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Full-width Hero Header */}
        <Box
          sx={{
            backgroundImage: `url(${termsBg})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center bottom',
            backgroundSize: 'cover',
            px: { xs: '24px', md: '64px' },
            py: { xs: '48px', md: '64px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            variant="h1"
            sx={{
              ...typography.display,
              color: color.charcoal.default,
              mb: '12px',
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            sx={{
              ...typography.bodySmall,
              color: color.charcoal['500'],
              mb: '4px',
            }}
          >
            Applicable to all Vineyard Listing and Commercial Partnership
            Agreements
          </Typography>
          <Typography
            sx={{ ...typography.caption, color: color.charcoal['500'] }}
          >
            Version 1.0 — Effective from 1 May 2026
          </Typography>
          <Typography
            sx={{
              ...typography.bodyText,
              color: color.charcoal.default,
              mt: '32px',
              lineHeight: '1.7',
              maxWidth: '760px',
            }}
          >
            These General Terms and Conditions (&ldquo;Terms&rdquo;) apply to
            all agreements entered into with UK Vineyard Guide Ltd (Company No.
            16832285) (&ldquo;UKVG&rdquo; or &ldquo;Vendor&rdquo;), including
            Vineyard Listing Agreements and Commercial Partnership Agreements.
            By signing an Agreement, the Partner agrees to be bound by these
            Terms.
          </Typography>
        </Box>

        {/* Sidebar + Content */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Sticky Sidebar */}
          <Box
            sx={{
              width: { xs: '100%', md: '350px' },
              flexShrink: 0,
              backgroundColor: color.grey['20'],
              margin: { xs: '0', md: '30px' },
              borderRadius: '8px',
              p: { xs: '24px', md: '34px 24px' },
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              boxSizing: 'border-box',
              position: 'sticky',
              top: '30px',
              overflowY: 'auto',
            }}
          >
            <nav>
              {SECTIONS.map((section) => (
                <NavLink
                  key={section.number}
                  number={section.number}
                  title={section.title}
                  isActive={activeSection === section.number}
                  onClick={() => scrollToSection(section.number)}
                />
              ))}
            </nav>
          </Box>

          {/* Scrollable content — all sections on one page */}
          <Box
            sx={{
              flex: 1,
              px: { xs: '24px', md: '36px' },
              pt: { xs: '40px', md: '64px' },
              pb: { xs: '64px', md: '24px' },
            }}
          >
            {SECTIONS.map(({ number }, index) => (
              <Box
                key={number}
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current[number] = el;
                }}
                sx={{
                  mb: '24px',
                  borderBottom:
                    index < SECTIONS.length - 1
                      ? `1px solid ${color.moss['100']}`
                      : 'none',
                }}
              >
                {number === 1 && <Section1 />}
                {number === 2 && <Section2 />}
                {number === 3 && <Section3 />}
                {number === 4 && <Section4 />}
                {number === 5 && <Section5 />}
                {number === 6 && <Section6 />}
                {number === 7 && <Section7 />}
                {number === 8 && <Section8 />}
                {number === 9 && <Section9 />}
                {number === 10 && <Section10 />}
                {number === 11 && <Section11 />}
              </Box>
            ))}

            {/* Footer */}
            <Box
              sx={{
                pt: '32px',
                borderTop: `1px solid ${color.charcoal['100']}`,
              }}
            >
              <Typography
                sx={{
                  ...typography.caption,
                  color: color.charcoal['500'],
                  lineHeight: '1.6',
                }}
              >
                UK Vineyard Guide Ltd | Company No. 16832285 | 71–75 Shelton
                Street, London WC2H 9JQ |
                https://ukvineyardguide.co.uk/contractterms
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// ─── Navigation Component ────────────────────────────────────────────────────────

interface NavLinkProps {
  number: number;
  title: string;
  isActive: boolean;
  onClick: () => void;
}

function NavLink({
  number,
  title,
  isActive,
  onClick,
}: NavLinkProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        mb: '20px',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
        transition: 'transform 0.15s ease',
        '&:hover': { transform: 'translateX(3px)' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '30px',
          height: '30px',
          borderRadius: '50%',
          backgroundColor: isActive ? color.moss.default : color.moss['100'],
          flexShrink: 0,
          transition: 'background-color 0.15s ease',
        }}
      >
        <Typography
          sx={{
            ...typography.caption,
            color: isActive ? 'white' : color.moss['500'],
            fontWeight: 350,
            lineHeight: 1,
          }}
        >
          {number}
        </Typography>
      </Box>
      <Typography
        sx={{
          ...typography.bodySmall,
          color: isActive ? color.charcoal.default : color.charcoal['500'],
          fontWeight: 350,
          pt: '7px',
          transition: 'color 0.15s ease',
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}
